/**
 * Speurtocht (Treasure Hunt) Types
 * Type definitions for the speurtocht game module
 */

/**
 * Speurtocht status
 */
export type SpeurttochtStatus = "draft" | "active" | "paused" | "completed" | "archived";

/**
 * Step types for speurtocht
 */
export type StepType = "location" | "photo" | "puzzle" | "word" | "qr_code";

/**
 * Session status
 */
export type SessionStatus = "active" | "completed" | "expired" | "abandoned";

/**
 * Step completion status
 */
export type StepCompletionStatus = "pending" | "verified" | "rejected" | "auto_verified";

/**
 * Location step configuration
 */
export interface LocationStepConfig {
  lat: number;
  lng: number;
  radius_meters: number;
  address?: string;
  show_map: boolean;
}

/**
 * Photo step configuration
 */
export interface PhotoStepConfig {
  requirements: string[];
  verification: "auto" | "manual";
}

/**
 * Puzzle step configuration
 */
export interface PuzzleStepConfig {
  question: string;
  answer: string;
  case_sensitive: boolean;
}

/**
 * Word step configuration
 */
export interface WordStepConfig {
  answer: string;
  case_sensitive: boolean;
  hint?: string;
}

/**
 * QR Code step configuration
 */
export interface QRCodeStepConfig {
  expected_value: string;
  location_hint?: string;
}

/**
 * Step configuration union type
 */
export type StepConfig =
  | LocationStepConfig
  | PhotoStepConfig
  | PuzzleStepConfig
  | WordStepConfig
  | QRCodeStepConfig;

/**
 * Notification configuration for speurtocht
 */
export interface SpeurttochtNotificationConfig {
  on_activated: {
    enabled: boolean;
    title: string;
    body: string;
    lottie_animation: string;
  };
  on_start: {
    enabled: boolean;
    notify_all_users: boolean;
    title: string;
    body: string;
  };
  on_step_complete: {
    enabled: boolean;
    notify_team: boolean;
    title: string;
    body: string;
  };
  on_complete: {
    enabled: boolean;
    notify_all_users: boolean;
    title: string;
    body: string;
  };
  on_expired?: {
    enabled: boolean;
    notify_team: boolean;
    notify_all_users: boolean;
    title: string;
    body: string;
    lottie_animation: string;
  };
}

/**
 * Default notification configuration
 */
export const DEFAULT_NOTIFICATION_CONFIG: SpeurttochtNotificationConfig = {
  on_activated: {
    enabled: true,
    title: "🗺️ Nieuwe Speurtocht!",
    body: '"{speurtocht_name}" is nu beschikbaar! Ga samen met je team op avontuur!',
    lottie_animation: "party",
  },
  on_start: {
    enabled: true,
    notify_all_users: true,
    title: "{team_name} is begonnen!",
    body: "{team_name} is begonnen aan {speurtocht_name}",
  },
  on_step_complete: {
    enabled: true,
    notify_team: true,
    title: "Stap voltooid! 🎉",
    body: "{user_name} heeft stap {step_number} voltooid",
  },
  on_complete: {
    enabled: true,
    notify_all_users: true,
    title: "{team_name} heeft gewonnen! 🏆",
    body: "{team_name} heeft {speurtocht_name} voltooid in {duration}",
  },
  on_expired: {
    enabled: true,
    notify_team: true,
    notify_all_users: true,
    title: "⏰ Tijd is op!",
    body: "{team_name} heeft {completed_steps}/{total_steps} stappen gehaald ({points} punten)",
    lottie_animation: "timer",
  },
};

/**
 * Speurtocht entity (from speurtochts table)
 */
export interface Speurtocht {
  id: string;
  weekend_id: string;
  name: string;
  description: string | null;
  icon: string;
  color: string;
  status: SpeurttochtStatus;
  available_from: string | null;
  available_until: string | null;
  has_time_limit: boolean;
  max_duration_minutes: number | null;
  points_per_step: number;
  completion_bonus: number;
  first_team_bonus: number;
  require_photo_verification: boolean;
  require_location_verification: boolean;
  show_map_to_users: boolean;
  show_all_steps: boolean;
  step_completion_lottie: string;
  speurtocht_completion_lottie: string;
  notification_config: SpeurttochtNotificationConfig;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Computed from joins
  steps_count?: number;
  creator?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
}

/**
 * Speurtocht step entity (from speurtocht_steps table)
 */
export interface SpeurttochtStep {
  id: string;
  speurtocht_id: string;
  step_order: number;
  order_index?: number;
  title: string;
  description: string | null;
  hint: string | null;
  step_type: StepType;
  config: StepConfig | Record<string, unknown>;
  points: number | null;
  /** Lottie animation to show when step is completed */
  lottie_animation: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Speurtocht session entity (from speurtocht_sessions table)
 */
export interface SpeurttochtSession {
  id: string;
  speurtocht_id: string;
  team_id: string;
  started_by: string;
  started_at: string;
  completed_at: string | null;
  expires_at: string | null;
  status: SessionStatus;
  current_step_index: number;
  total_steps: number;
  completed_steps: number;
  total_points: number;
  bonus_points: number;
  duration_seconds: number | null;
  created_at: string;
  updated_at: string;
  // Joined data
  speurtocht?: Speurtocht;
  team?: {
    id: string;
    name: string;
    color: string | null;
    avatar_url: string | null;
  };
  started_by_user?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  completions?: SpeurttochtStepCompletion[];
}

/**
 * Step completion entity (from speurtocht_step_completions table)
 */
export interface SpeurttochtStepCompletion {
  id: string;
  session_id: string;
  step_id: string;
  completed_by: string;
  completed_at: string;
  submission: StepSubmission;
  submission_data?: Record<string, unknown>;
  status: StepCompletionStatus;
  is_verified: boolean | null;
  verified_by: string | null;
  verified_at: string | null;
  rejection_reason: string | null;
  points_earned: number;
  created_at: string;
  // Joined data
  step?: SpeurttochtStep;
  completed_by_user?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
}

/**
 * Submission data (varies by step type)
 */
export interface LocationSubmission {
  lat: number;
  lng: number;
  accuracy: number;
  distance_from_target: number;
}

export interface PhotoSubmission {
  photo_url: string;
  thumbnail_url?: string;
}

export interface PuzzleSubmission {
  answer: string;
}

export interface WordSubmission {
  answer: string;
}

export interface QRCodeSubmission {
  scanned_value: string;
}

export type StepSubmission =
  | LocationSubmission
  | PhotoSubmission
  | PuzzleSubmission
  | WordSubmission
  | QRCodeSubmission
  | Record<string, unknown>;

/**
 * Complete step result from RPC
 */
export interface CompleteStepResult {
  completion_id: string;
  points_earned: number;
  status: StepCompletionStatus;
  is_last_step: boolean;
  is_first_team: boolean;
  bonus_points: number;
  total_points: number;
}

/**
 * Active session data for dashboard card
 */
export interface ActiveSessionData {
  session_id: string;
  speurtocht_id: string;
  speurtocht_name: string;
  speurtocht_icon: string;
  speurtocht_color: string;
  current_step_index: number;
  total_steps: number;
  completed_steps: number;
  started_at: string;
  expires_at: string | null;
  total_points: number;
}

/**
 * Team progress for admin monitoring
 */
export interface TeamProgress {
  team_id: string;
  team_name: string;
  team_color: string | null;
  session_id: string | null;
  status: SessionStatus | null;
  current_step_index: number;
  completed_steps: number;
  total_steps: number;
  total_points: number;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  is_first_team: boolean;
  completions: {
    step_id: string;
    step_order: number;
    completed_by: string;
    completed_by_name: string;
    status: StepCompletionStatus;
    points_earned: number;
    completed_at: string;
  }[];
}

/**
 * Speurtocht with steps (for play screen)
 */
export interface SpeurttochtWithSteps extends Speurtocht {
  steps: SpeurttochtStep[];
}

/**
 * Speurtocht with session (for user view)
 */
export interface SpeurttochtWithSession extends Speurtocht {
  session?: SpeurttochtSession;
  can_start: boolean;
  start_reason?: "already_started" | "not_available_yet" | "ended" | "no_team";
}

/**
 * Step icons by type
 */
export const STEP_TYPE_ICONS: Record<StepType, string> = {
  location: "location",
  photo: "camera",
  puzzle: "help-circle",
  word: "text",
  qr_code: "qr-code",
};

/**
 * Step type labels (Dutch)
 */
export const STEP_TYPE_LABELS: Record<StepType, string> = {
  location: "Ga naar locatie",
  photo: "Maak een foto",
  puzzle: "Los de puzzel op",
  word: "Voer het woord in",
  qr_code: "Scan QR-code",
};

/**
 * Step type colors
 */
export const STEP_TYPE_COLORS: Record<StepType, string> = {
  location: "#10B981", // Green
  photo: "#3B82F6", // Blue
  puzzle: "#8B5CF6", // Purple
  word: "#F59E0B", // Amber
  qr_code: "#EC4899", // Pink
};

/**
 * Step type configuration (icon + label combined)
 */
export const STEP_TYPE_CONFIG: Record<StepType, { icon: string; label: string }> = {
  location: { icon: "location", label: "Ga naar locatie" },
  photo: { icon: "camera", label: "Maak een foto" },
  puzzle: { icon: "help-circle", label: "Los de puzzel op" },
  word: { icon: "text", label: "Voer het woord in" },
  qr_code: { icon: "qr-code", label: "Scan QR-code" },
};

/**
 * Create speurtocht parameters
 */
export interface CreateSpeurttochtParams {
  weekend_id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  available_from?: string;
  available_until?: string;
  has_time_limit?: boolean;
  max_duration_minutes?: number;
  points_per_step?: number;
  completion_bonus?: number;
  first_team_bonus?: number;
  require_photo_verification?: boolean;
  require_location_verification?: boolean;
  show_map_to_users?: boolean;
  show_all_steps?: boolean;
  step_completion_lottie?: string;
  speurtocht_completion_lottie?: string;
  notification_config?: Partial<SpeurttochtNotificationConfig>;
}

/**
 * Create step parameters
 */
export interface CreateStepParams {
  speurtocht_id: string;
  step_order: number;
  title: string;
  description?: string;
  hint?: string;
  step_type: StepType;
  config: StepConfig;
  points?: number;
  lottie_animation?: string;
}

/**
 * Points configuration for speurtocht
 */
export interface SpeurttochtPointsConfig {
  points_per_step: number;
  completion_bonus: number;
  first_team_bonus: number;
}

export const DEFAULT_POINTS_CONFIG: SpeurttochtPointsConfig = {
  points_per_step: 10,
  completion_bonus: 50,
  first_team_bonus: 100,
};

